import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import {
  TrendingUp, DollarSign, Target, Zap, BarChart3, Sliders, Info, ChevronDown, ChevronUp, AlertCircle,
  LoaderCircle
} from 'lucide-react'
import { Switch } from '@/features/shadcn/ui/switch'
import { formatCompactCurrency } from '../utils'
import { Button } from '@/features/shadcn/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/features/shadcn/ui/tooltip'

import {
  fetchMMMSummary,
  fetchMMMTrainStatus,
  selectMMMData,
  selectMMMDataStatus,
  selectMMMDataError,
  selectMMMJobId,
  selectMMMProgress,
} from '../trafficReportSlice'
import { selectRootDomains } from '@/rootSlice'
import { AppDispatch } from '@/app/store'
import {
  SummaryCard,
  ChannelROICard,
  ContributionChart,
  ModelFitChart,
  BudgetComparisonChart,
  BudgetSlider,
  AdstockSaturationCharts,
} from '@/sharedComponents/MMM'

interface MMMTabProps {
  currency?: string
  timePeriodWatch?: string
  customDateRangeWatch?: { start?: string; end?: string }
}

const TRAIN_STATUS_POLL_INTERVAL_MS = 10000
const MMM_MIN_DAYS = 30 // Minimum days for Marketing Mix API calls

/**
 * Ensure minimum 30 days for MMM API calls.
 * Returns adjusted timePeriod and customDateRange that ensures at least 30 days,
 * but doesn't change the displayed values.
 */
function ensureMinimumDaysForMMM(
  timePeriod?: string,
  customDateRange?: { start?: string; end?: string }
): { timePeriod?: string; customDateRange?: { start?: string; end?: string } } {
  // If custom date range, calculate days and extend if needed
  if (timePeriod === 'custom' && customDateRange?.start && customDateRange?.end) {
    const start = moment(customDateRange.start)
    const end = moment(customDateRange.end)
    const days = end.diff(start, 'days') + 1
    
    if (days < MMM_MIN_DAYS) {
      // Extend the start date backwards to ensure 30 days minimum
      const adjustedStart = end.clone().subtract(MMM_MIN_DAYS - 1, 'days').format('YYYY-MM-DD')
      return {
        timePeriod: 'custom',
        customDateRange: {
          start: adjustedStart,
          end: customDateRange.end,
        },
      }
    }
    return { timePeriod, customDateRange }
  }
  
  // For preset time periods, check if they're less than 30 days
  const timePeriodDays: Record<string, number> = {
    'today': 1,
    'yesterday': 1,
    '24-hours': 1,
    '5-minutes': 0.003, // Less than 1 day
    '10-minutes': 0.007,
    '30-minutes': 0.021,
    '60-minutes': 0.042,
    '7-days': 7,
    '14-days': 14,
    '30-days': 30,
    '90-days': 90,
  }
  
  const days = timePeriodDays[timePeriod || '']
  if (days !== undefined && days < MMM_MIN_DAYS) {
    // Use 30-days minimum for MMM API
    return { timePeriod: '30-days', customDateRange: undefined }
  }
  
  // If timePeriod is not in our map, assume it's less than 30 days and use 30-days
  // Also handle undefined/null by defaulting to 30-days
  if (!timePeriod || days === undefined) {
    return { timePeriod: '30-days', customDateRange: undefined }
  }
  
  // If >= 30 days, use as-is
  return { timePeriod, customDateRange }
}

export default function MMMTab({ currency = 'USD', timePeriodWatch, customDateRangeWatch }: MMMTabProps) {
  const dispatch = useDispatch<AppDispatch>()

  const mmmData = useSelector(selectMMMData)
  const mmmDataStatus = useSelector(selectMMMDataStatus)
  const mmmDataError = useSelector(selectMMMDataError)
  const mmmJobId = useSelector(selectMMMJobId)
  const mmmProgress = useSelector(selectMMMProgress)
  const domains = useSelector(selectRootDomains)

  const [selectedScenario, setSelectedScenario] = useState(0)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [simulatorBudgets, setSimulatorBudgets] = useState<Record<string, number>>({})
  const [useCampaigns, setUseCampaigns] = useState(false)

  const mmmApiParams = useMemo(
    () => ensureMinimumDaysForMMM(timePeriodWatch, customDateRangeWatch),
    [timePeriodWatch, customDateRangeWatch]
  )

  const isFetchingRef = useRef(false)
  const lastFetchKeyRef = useRef<string>('')

  const runFetch = useCallback(() => {
    if (isFetchingRef.current) return
    const domain = domains?.length ? domains[0] : undefined
    const key = `${mmmApiParams.timePeriod ?? ''}-${mmmApiParams.customDateRange?.start ?? ''}-${mmmApiParams.customDateRange?.end ?? ''}-${useCampaigns}-${domain ?? ''}`
    if (lastFetchKeyRef.current === key) return
    lastFetchKeyRef.current = key
    isFetchingRef.current = true
    dispatch(
      fetchMMMSummary({
        timePeriod: mmmApiParams.timePeriod,
        customDateRange: mmmApiParams.customDateRange,
        useCampaigns,
        domain,
      })
    )
    setTimeout(() => { isFetchingRef.current = false }, 500)
  }, [dispatch, mmmApiParams, useCampaigns, domains])

  useEffect(() => {
    if (mmmDataStatus === 'idle') runFetch()
  }, [mmmDataStatus, runFetch])

  const rangeKey = customDateRangeWatch ? `${customDateRangeWatch.start ?? ''}-${customDateRangeWatch.end ?? ''}` : ''
  const prevParamsRef = useRef<{ timePeriod?: string; rangeKey: string; useCampaigns: boolean }>({
    timePeriod: timePeriodWatch,
    rangeKey,
    useCampaigns,
  })
  useEffect(() => {
    const changed =
      prevParamsRef.current.timePeriod !== timePeriodWatch ||
      prevParamsRef.current.rangeKey !== rangeKey ||
      prevParamsRef.current.useCampaigns !== useCampaigns
    prevParamsRef.current = { timePeriod: timePeriodWatch, rangeKey, useCampaigns }
    if (changed && (mmmDataStatus === 'succeeded' || mmmDataStatus === 'failed')) {
      lastFetchKeyRef.current = ''
      runFetch()
    }
  }, [timePeriodWatch, rangeKey, useCampaigns, mmmDataStatus, runFetch])

  useEffect(() => {
    if (mmmDataStatus !== 'training' || !mmmJobId) return
    const poll = () => dispatch(fetchMMMTrainStatus(mmmJobId))
    const id = setInterval(poll, TRAIN_STATUS_POLL_INTERVAL_MS)
    poll()
    return () => clearInterval(id)
  }, [dispatch, mmmDataStatus, mmmJobId])

  const prevJobIdRef = useRef<string | undefined>()
  useEffect(() => {
    if (mmmDataStatus === 'idle' && prevJobIdRef.current && !mmmJobId) {
      lastFetchKeyRef.current = ''
      runFetch()
    }
    prevJobIdRef.current = mmmJobId
  }, [mmmDataStatus, mmmJobId, runFetch])
  
  // Initialize simulator budgets when data loads
  useEffect(() => {
    if (mmmData?.budgetScenarios?.[0]?.allocations) {
      // Deep clone to avoid reference issues
      setSimulatorBudgets({ ...mmmData.budgetScenarios[0].allocations })
    }
  }, [mmmData])
  
  const data = mmmData

  const totalSimulatorBudget = useMemo(() => 
    Object.values(simulatorBudgets).reduce((sum, val) => sum + val, 0),
    [simulatorBudgets]
  )

  // Calculate simulated revenue based on budget changes
  const simulatedRevenue = useMemo(() => {
    if (!data?.summary || !data?.channels || !data?.budgetScenarios?.[0]) return 0
    let revenue = data.summary.baselineContribution
    data.channels.forEach((channel: any) => {
      const spend = simulatorBudgets[channel.channelId] || 0
      const originalSpend = data.budgetScenarios[0].allocations[channel.channelId] || 1
      const ratio = spend / originalSpend
      // Apply diminishing returns
      const effectiveRatio = Math.pow(ratio, 0.7)
      revenue += channel.attributedRevenue * effectiveRatio
    })
    return revenue
  }, [simulatorBudgets, data])

  const simulatedRoas = totalSimulatorBudget > 0 ? simulatedRevenue / totalSimulatorBudget : 0

  const blendedRoas = useMemo((): number => {
    const scenario = data?.budgetScenarios?.[0]
    const total: number = scenario ? (Object.values(scenario.allocations).reduce((a: number, b: number) => a + b, 0) as number) : 0
    const mediaContribution: number = Number(data?.summary?.totalMediaContribution ?? 0)
    return total > 0 ? mediaContribution / total : 0
  }, [data])

  // Loading state
  if (mmmDataStatus === 'loading') {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-500">
          <LoaderCircle className="tw-w-5 tw-h-5 tw-animate-spin" />
          <span>Loading Marketing Mix Model data...</span>
        </div>
      </div>
    )
  }

  // Training state - auto-triggered on first visit
  if (mmmDataStatus === 'training') {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center tw-max-w-md tw-w-full tw-px-4">
          <LoaderCircle className="tw-w-12 tw-h-12 tw-text-blue-500 tw-animate-spin" />
          <div className="tw-w-full">
            <h3 className="tw-font-semibold tw-text-slate-900 tw-mb-1">Training your Marketing Mix Model</h3>
            <p className="tw-text-sm tw-text-slate-600 tw-mb-4">
              We're building your model from your sales and ad spend data. This usually takes 10–30 minutes.
            </p>
            
            {/* Progress Bar */}
            <div className="tw-w-full tw-bg-slate-200 tw-rounded-full tw-h-2 tw-mb-2">
              <div 
                className="tw-bg-blue-500 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300"
                style={{ width: `${mmmProgress}%` }}
              />
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-text-slate-500">
              <span>Progress</span>
              <span>{mmmProgress}%</span>
            </div>
            
            <p className="tw-text-xs tw-text-slate-500 tw-mt-4">
              We'll automatically refresh when training completes.
            </p>
          </div>
          {mmmJobId && (
            <p className="tw-text-xs tw-text-slate-400">Job ID: {mmmJobId}</p>
          )}
        </div>
      </div>
    )
  }

  // Error state - no mock fallback
  // Only show error if status is explicitly 'failed' (not just missing data, which could be loading)
  if (mmmDataStatus === 'failed') {
    // Determine if this is a recoverable error
    const isRecoverable = !mmmDataError?.includes('Connect a store') && 
                          !mmmDataError?.includes('Insufficient data')
    
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center tw-max-w-md">
          <AlertCircle className="tw-w-12 tw-h-12 tw-text-red-500" />
          <div>
            <h3 className="tw-font-semibold tw-text-slate-900 tw-mb-1">Unable to load Marketing Mix data</h3>
            <p className="tw-text-sm tw-text-slate-600 tw-whitespace-pre-line">
              {mmmDataError || 'Connect a store (Shopify, Bigcommerce, etc.) to use Marketing Mix. The MMM service may also be unreachable.'}
            </p>
            {isRecoverable && (
              <p className="tw-text-xs tw-text-slate-500 tw-mt-2">
                If this problem persists, please contact support.
              </p>
            )}
          </div>
          {isRecoverable && (
            <Button
              variant="outline"
              onClick={() => {
                lastFetchKeyRef.current = ''
                isFetchingRef.current = false
                dispatch(
                  fetchMMMSummary({
                    timePeriod: mmmApiParams.timePeriod,
                    customDateRange: mmmApiParams.customDateRange,
                    useCampaigns,
                    domain: domains?.length ? domains[0] : undefined,
                  })
                )
              }}
            >
              Retry
            </Button>
          )}
        </div>
      </div>
    )
  }

  // If we have no data but status is not failed/loading/training, show loading (likely transitioning)
  if (!data && mmmDataStatus !== 'loading' && mmmDataStatus !== 'training' && mmmDataStatus !== 'failed') {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-500">
          <LoaderCircle className="tw-w-5 tw-h-5 tw-animate-spin" />
          <span>Loading Marketing Mix Model data...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="tw-space-y-6">
      {/* Toggle Switch for Channel vs Campaign */}
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-2 tw-bg-slate-50 tw-rounded-lg tw-border tw-border-slate-200">
        <label htmlFor="campaign-toggle" className="tw-text-sm tw-font-medium tw-text-slate-700">
          Channel Level
        </label>
        <Switch
          checked={useCampaigns}
          onCheckedChange={setUseCampaigns}
        />
        <label htmlFor="campaign-toggle" className="tw-text-sm tw-font-medium tw-text-slate-700">
          Campaign Level
        </label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="tw-inline-flex tw-cursor-help">
                <Info className="tw-w-4 tw-h-4 tw-text-slate-400" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="tw-max-w-xs">
                {useCampaigns
                  ? 'Analyzing individual campaigns. Each campaign will be treated as a separate channel for more granular insights.'
                  : 'Analyzing at channel level. All campaigns within a platform (e.g., Google Ads) are aggregated together.'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {/* Model Info Banner */}
      <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-indigo-50 tw-rounded-lg tw-border tw-border-blue-200 tw-p-4">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-p-2 tw-bg-blue-100 tw-rounded-lg">
              <BarChart3 className="tw-w-5 tw-h-5 tw-text-blue-600" />
            </div>
            <div>
              <h3 className="tw-font-semibold tw-text-slate-900">Marketing Mix Model</h3>
              <p className="tw-text-sm tw-text-slate-600">
                Trained on {data?.modelInfo?.dataRange?.start ?? '—'} to {data?.modelInfo?.dataRange?.end ?? '—'} ({data?.modelInfo?.granularity ?? '—'} data)
              </p>
            </div>
          </div>
            <div className="tw-flex tw-items-center tw-gap-6">
            <div className="tw-text-center">
              <p className="tw-text-xs tw-text-slate-500">Model Fit (R²)</p>
              <p className="tw-text-lg tw-font-bold tw-text-green-600">{data?.modelInfo?.rSquared != null ? `${((data.modelInfo.rSquared) * 100).toFixed(0)}%` : '—'}</p>
            </div>
            <div className="tw-text-center">
              <p className="tw-text-xs tw-text-slate-500">MAPE</p>
              <p className="tw-text-lg tw-font-bold tw-text-blue-600">{data?.modelInfo?.mape != null ? `${((data.modelInfo.mape) * 100).toFixed(1)}%` : '—'}</p>
            </div>
            <div className="tw-text-center">
              <p className="tw-text-xs tw-text-slate-500">Last Updated</p>
              <p className="tw-text-sm tw-font-medium tw-text-slate-700">{data?.modelInfo?.trainingDate ?? '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
        <SummaryCard
          title="Total Revenue"
          value={data?.summary?.totalRevenue ?? 0}
          format="currency"
          icon={DollarSign}
          color="green"
          currency={currency}
        />
        <SummaryCard
          title="Media Contribution"
          value={data?.summary?.mediaContributionPct ?? 0}
          format="percentage"
          subtitle={data?.summary?.totalMediaContribution != null ? formatCompactCurrency(data.summary.totalMediaContribution, currency) : undefined}
          icon={Target}
          color="blue"
          currency={currency}
        />
        <SummaryCard
          title="Baseline Contribution"
          value={data?.summary?.baselineContributionPct ?? 0}
          format="percentage"
          subtitle={data?.summary?.baselineContribution != null ? formatCompactCurrency(data.summary.baselineContribution, currency) : undefined}
          icon={TrendingUp}
          color="purple"
          currency={currency}
        />
        <SummaryCard
          title="Blended ROAS"
          value={blendedRoas}
          format="roas"
          icon={Zap}
          color="orange"
          currency={currency}
        />
      </div>

      {/* Main Content Grid */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
        {/* Channel ROI Cards */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Channel Performance</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="tw-inline-flex tw-cursor-help">
                    <Info className="tw-w-4 tw-h-4 tw-text-slate-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="tw-max-w-xs">ROI estimates with 90% confidence intervals from Bayesian inference</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="tw-space-y-3">
            {(data?.channels ?? []).map((channel) => (
              <ChannelROICard key={channel.channelId} channel={channel} currency={currency} />
            ))}
          </div>
        </div>

        {/* Contribution Breakdown */}
        <ContributionChart channels={data?.channels ?? []} currency={currency} />
      </div>

      {/* Model Fit Chart */}
      <ModelFitChart weeklyData={data?.weeklyData ?? []} currency={currency} />

      {/* Budget Scenarios */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
          <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Budget Scenarios</h3>
          <div className="tw-flex tw-gap-2">
            {(data?.budgetScenarios ?? []).map((scenario, index) => (
              <button
                key={scenario.name}
                onClick={() => {
                  setSelectedScenario(index)
                  setSimulatorBudgets(scenario.allocations)
                }}
                className={`tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors ${
                  selectedScenario === index
                    ? 'tw-bg-blue-500 tw-text-white'
                    : 'tw-bg-slate-100 tw-text-slate-700 hover:tw-bg-slate-200'
                }`}
              >
                {scenario.name}
              </button>
            ))}
          </div>
        </div>

        <BudgetComparisonChart 
          scenarios={data?.budgetScenarios ?? []} 
          selectedIndex={selectedScenario}
          channels={data?.channels ?? []}
          currency={currency}
        />
      </div>

      {/* Budget Simulator */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-p-2 tw-bg-purple-100 tw-rounded-lg">
              <Sliders className="tw-w-5 tw-h-5 tw-text-purple-600" />
            </div>
            <div>
              <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Budget Simulator</h3>
              <p className="tw-text-sm tw-text-slate-500">Adjust channel budgets to see projected impact</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const optimized = data.budgetScenarios?.[1]
              if (optimized?.allocations) setSimulatorBudgets(optimized.allocations)
            }}
            disabled={!data?.budgetScenarios?.[1]?.allocations}
          >
            Apply Optimized
          </Button>
        </div>

        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6">
          {/* Sliders */}
          <div className="lg:tw-col-span-2 tw-space-y-4">
            {(data?.channels ?? []).map((channel) => {
              // Use channel's totalSpend as fallback if simulatorBudgets hasn't been initialized yet
              const currentValue = simulatorBudgets[channel.channelId] ?? channel.totalSpend ?? 0
              return (
                <BudgetSlider
                  key={channel.channelId}
                  channel={channel}
                  value={currentValue}
                  onChange={(value) => setSimulatorBudgets(prev => ({ ...prev, [channel.channelId]: value }))}
                  currency={currency}
                />
              )
            })}
          </div>

          {/* Simulation Results */}
          <div className="tw-bg-slate-50 tw-rounded-lg tw-p-4">
            <h4 className="tw-font-semibold tw-text-slate-900 tw-mb-4">Projected Outcome</h4>
            <div className="tw-space-y-4">
              <div>
                <p className="tw-text-sm tw-text-slate-500">Total Budget</p>
                <p className="tw-text-2xl tw-font-bold tw-text-slate-900">
                  {formatCompactCurrency(totalSimulatorBudget, currency)}
                </p>
              </div>
              <div>
                <p className="tw-text-sm tw-text-slate-500">Expected Revenue</p>
                <p className="tw-text-2xl tw-font-bold tw-text-green-600">
                  {formatCompactCurrency(simulatedRevenue, currency)}
                </p>
                <p className="tw-text-sm tw-text-slate-500">
                  {(data?.summary?.totalRevenue ?? 0) < simulatedRevenue ? '+' : ''}
                  {formatCompactCurrency(simulatedRevenue - (data?.summary?.totalRevenue ?? 0), currency)} vs current
                </p>
              </div>
              <div>
                <p className="tw-text-sm tw-text-slate-500">Expected ROAS</p>
                <p className={`tw-text-2xl tw-font-bold ${simulatedRoas > 2 ? 'tw-text-green-600' : 'tw-text-orange-600'}`}>
                  {simulatedRoas.toFixed(2)}x
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Parameters (Collapsible) */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="tw-w-full tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between hover:tw-bg-slate-50 tw-transition-colors"
        >
          <div className="tw-flex tw-items-center tw-gap-3">
            <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Advanced Model Parameters</h3>
            <span className="tw-text-xs tw-bg-slate-100 tw-text-slate-600 tw-px-2 tw-py-1 tw-rounded">Technical</span>
          </div>
          {showAdvanced ? (
            <ChevronUp className="tw-w-5 tw-h-5 tw-text-slate-400" />
          ) : (
            <ChevronDown className="tw-w-5 tw-h-5 tw-text-slate-400" />
          )}
        </button>

        {showAdvanced && (
          <div className="tw-px-6 tw-pb-6 tw-border-t tw-border-slate-100">
            <div className="tw-pt-4">
              <AdstockSaturationCharts channels={data?.channels ?? []} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
